import { appConfig } from "@/config";
import { StreamHttpError, apiErrorBodyMessage, streamNdjson } from "@/core/api";
import { useAppDispatch } from "@/hooks/redux";
import {
  captureApi,
  CaptureApiTagType,
} from "@/modules/capture/services/captureApi";
import type {
  PredictedRectangle,
  RunVisionRequest,
  VisionFeedbackRequest,
  VisionRunResponse,
  VisionStreamEvent,
  VisionStreamPhaseEnum,
} from "@repo/schema";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "react-redux";

const { predict } = appConfig.studioApi.endpoints;

/**
 * What a live turn tells the page. Every callback except `onDone` describes a
 * PREVIEW and may be ignored entirely; `onDone` carries the real result.
 */
export interface VisionStreamCallbacks {
  onPhase(phase: VisionStreamPhaseEnum): void;
  /** A delta of the model's thought summary, not the whole thing so far. */
  onThought(text: string): void;
  onBox(rectangle: PredictedRectangle): void;
  onDone(result: VisionRunResponse): void;
  /** Never fired for a cancellation — the caller already knows about those. */
  onError(message: string, status?: number): void;
}

/** Shaped like the RTK Query trigger handle the page already keeps a ref to. */
export interface VisionStreamHandle {
  abort: () => void;
}

/**
 * Run a Robopipe Vision turn over the streaming routes.
 *
 * The non-streaming `runVisionDetection` mutation in `labelApi` stays: the
 * batch fan-out still uses it, because nobody is watching those forty images
 * and forty open streams would buy nothing. This is for the run the labeller is
 * sitting in front of.
 *
 * Two things this owns that the mutation used to do for free:
 *
 * - **Cancellation.** An `AbortController` in place of the RTK handle. It stops
 *   the browser reading, not the model — the turn finishes server-side and
 *   stages its boxes either way, which is why cancelling is cheap to offer and
 *   why an aborted run is not an error.
 * - **Cache invalidation.** Nothing here passes through RTK Query, so
 *   `invalidateAfterRun` never fires. The same tags are dispatched by hand when
 *   a turn completes; without them the awaiting-review chip on the image list
 *   would go stale after every run.
 */
export const useVisionStream = () => {
  const dispatch = useAppDispatch();
  // The store rather than a selector: `refreshSession` wants `getState`, and
  // subscribing this hook to state it never reads would re-render the label
  // page on every unrelated change.
  const store = useStore();
  const { t } = useTranslation(["label", "common"]);

  const drive = useCallback(
    (
      url: string,
      body: unknown,
      callbacks: VisionStreamCallbacks,
      invalidate: () => void,
    ): VisionStreamHandle => {
      const controller = new AbortController();

      void (async () => {
        try {
          const events = streamNdjson<VisionStreamEvent>({
            url,
            body,
            signal: controller.signal,
            auth: { dispatch, getState: store.getState },
          });
          for await (const event of events) {
            switch (event.type) {
              case "phase":
                callbacks.onPhase(event.phase);
                break;
              case "thought":
                callbacks.onThought(event.text);
                break;
              case "box":
                callbacks.onBox(event.rectangle);
                break;
              case "done":
                invalidate();
                callbacks.onDone(event.result);
                break;
              case "error":
                // A failure the server could no longer express as a status,
                // because the turn had already started streaming.
                invalidate();
                callbacks.onError(
                  apiErrorBodyMessage({
                    statusCode: event.status,
                    code: event.code,
                    message: event.message,
                    params: event.params,
                  }),
                  event.status,
                );
                break;
            }
          }
        } catch (error: unknown) {
          // Cancelling is not failing. The page has already put its own
          // "cancelled" card up and bumped the run id.
          if (controller.signal.aborted) return;
          if (error instanceof StreamHttpError) {
            callbacks.onError(apiErrorBodyMessage(error.body), error.status);
            return;
          }
          // Anything else is the transport giving out (a dropped connection
          // rejects fetch or the body read) or a torn line. The browser's own
          // message ("Failed to fetch", "Load failed") is English and says
          // nothing the labeller can act on.
          callbacks.onError(t("page.connectionLost"));
        }
      })();

      return { abort: () => controller.abort() };
    },
    [dispatch, store, t],
  );

  /**
   * The same tags `invalidateAfterRun` provides for the non-streaming routes.
   * No `batchId` branch: a batch never takes this path.
   */
  const invalidateFor = useCallback(
    (projectId: number, taskId: number) => () => {
      dispatch(
        captureApi.util.invalidateTags([
          { type: CaptureApiTagType.Tasks, id: projectId },
          { type: CaptureApiTagType.Tasks, id: `detail-${taskId}` },
        ]),
      );
    },
    [dispatch],
  );

  const runDetection = useCallback(
    (
      args: { projectId: number; taskId: number; body: RunVisionRequest },
      callbacks: VisionStreamCallbacks,
    ): VisionStreamHandle =>
      drive(
        predict.visionStream(args.projectId, args.taskId),
        args.body,
        callbacks,
        invalidateFor(args.projectId, args.taskId),
      ),
    [drive, invalidateFor],
  );

  const sendFeedback = useCallback(
    (
      args: {
        projectId: number;
        taskId: number;
        conversationId: number;
        body: VisionFeedbackRequest;
      },
      callbacks: VisionStreamCallbacks,
    ): VisionStreamHandle =>
      drive(
        predict.visionFeedbackStream(
          args.projectId,
          args.taskId,
          args.conversationId,
        ),
        args.body,
        callbacks,
        invalidateFor(args.projectId, args.taskId),
      ),
    [drive, invalidateFor],
  );

  return { runDetection, sendFeedback };
};
